/** @jsxImportSource @emotion/react */
import profileStub from '../../../../assets/images/profilePlaceholder.jpg';
import * as React from 'react';
import * as R from 'ramda';
import { GuestDetails } from '../../../guestDetails';
import { connect } from 'react-redux';
import { IAppState } from '../../../../core/redux/IAppState';
import { Column, Row } from '../../../../layout/flexbox';
import { CIRCULAR_RADIUS } from '../../../guestCard/guestCardStyles';
import { css } from '@emotion/react';
import { BLUE_CREEK, COOL_BLUE, COOL_GREY, GREEN_CREEK, RED_CREEK } from '../../../../styles/colors';
import { fullName } from '../../../module';
import StatusButton from '../../../../forms/elements/StatusButton';
import { doNothing, isNilOrEmpty } from '../../../../language/module';
import { XLargeText } from '../../../../styles/styles';
import { ROW_SPACING } from '../../../../forms/styles';
import TabSelectDiv from '../../../../layout/TabSelectDiv';
import ErrorView from '../../../../forms/ErrorView';
import { BoxedErrorInverseNoBorder } from '../../../../forms/elements/boxedMessage/boxedMessageStyles';
import { ACTION_TYPE } from '../../../../core/redux/actionType';
import { getLoading } from '../../../../core/redux/api/module';
import { onIe } from '../../../../language/browser';
import { hasPhoto } from '../../../guestPhoto/module';
import { getResource } from '../../../../language/resources/getResource';
import { Dropdown } from '../../../../forms/elements/select/Dropdown';
import ImmutableMap from '../../../../language/ImmutableMap';
import { EntitlementType } from '../../../../entitlementTypes/entitlementType';
import { QuickEntitlement } from '../../models';
import { calculatePassReprintFee, NO_REPRINT_FEE } from './module';
import { assoc } from 'ramda';
import { PassPrintConfig } from '../../../../auth/config/passPrintConfig';


const ieCheckbox = !onIe() ? '' : css`margin-top: -14px;`;

const NameText = css`
  ${XLargeText};
  user-select: none;
  overflow-wrap: break-word;
  word-break: break-word;
  width: 250px;
`;

const GuestPrintSelect = (eligibilityError: string | undefined, isSelected: boolean) => css`
  padding: 10px 20px;
  transition: all .3s ease;
  cursor: ${eligibilityError ? 'not-allowed' : 'pointer'};
  box-sizing: border-box;

  &:hover { 
    background-color: ${COOL_BLUE} !important;
  }

  ${isSelected ? css`border-right: 8px solid #0080BB !important;` : ''};
  ${eligibilityError ? css`background-color: ${COOL_GREY} !important;` : ''};
`;

const GuestPrintStatusButton = css`
  flex-basis: 75px; 
  flex-shrink: 0; 
  user-select: none;
`;

const ReprintFeeBox = (fee: number | undefined) => css`
  margin-left: 10px;
  margin-right: 35px !important;
  width: 50px;
  min-height: 20px;
  background-color: ${((fee !== undefined && fee > NO_REPRINT_FEE) || fee === undefined) ? RED_CREEK : BLUE_CREEK};
  border-radius: 5px;
  text-align: center;
  padding: 7px 0px !important;
  color: WHITE;
`;

type Props = {
  guestId: string;
  isSelected: boolean;
  onSelect: (guest: GuestDetails, isEligible: boolean) => () => void;
  loadingPhoto?: boolean;
  eligibilityError?: string;
  guest?: GuestDetails;
  resources: Record<string, any>;
  selectedEntitlements: ImmutableMap<string>;
  onChange: (key: string, value: string) => void;
  allEntitlementTypes?: ImmutableMap<EntitlementType>;
  enablePassReprintNotifications: boolean;
  passPrintConfig?: PassPrintConfig;
};

type State = {
  passReprintFee: number | undefined;
};

/**
 * PrintUserSummary: a component that renders one of the rows in the table of guests in a household when doing group
 * print.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  (state: IAppState, props: Props) => ({
    guest:        state.guests.get(props.guestId),
    loadingPhoto: !!getLoading(state, props.guestId, ACTION_TYPE.GET_GUEST_PHOTO_REQUEST)
                  || !!getLoading(state, props.guestId, ACTION_TYPE.SET_GUEST_PHOTO_REQUEST),
    allEntitlementTypes:  state.entitlementTypesDetails && state.entitlementTypesDetails.allEntitlementTypes,
    enablePassReprintNotifications: state.resortToolsConfig.enablePassReprintNotifications,
    passPrintConfig: state.resortToolsConfig.passPrintConfig,
  }),
)(class PrintUserSummary extends React.PureComponent<Props, State> {
  public state = {
    passReprintFee : undefined
  };

  componentDidMount() {
    if (!this.props.selectedEntitlements.has(this.props.guestId)) {
      const entitlement = this.props.guest?.entitlements?.filter(e => e.entitlementCategoryId === 1 && e.entitlementStatusId === 1 && Date.parse(e.expirationDate) > Date.now())[0];
      this.props.onChange(this.props.guestId, entitlement ? entitlement as any : '');
    }
    this.calculatePassReprintFee();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {  
    if (this.props.guest?.passReprintCount !== prevProps.guest?.passReprintCount)  {
      this.calculatePassReprintFee();
    }
  }

  public calculatePassReprintFee = () => {
    /* Calculate reprint fee - if unable to load pass reprints - default to undefined */
    if (this.props.guest?.passReprintCount !== undefined) {
      this.setState((prev) => assoc("passReprintFee", calculatePassReprintFee(this.props.guest!.passReprintCount!, this.props.passPrintConfig?.defaultPassPrintFee), prev));
    } else {
      this.setState((prev) => assoc("passReprintFee", undefined, prev));
    }     
  };

  public entitlements = Array.from(
    new Map<number, QuickEntitlement>(
      this.props.guest?.entitlements
        ?.filter(e => e.entitlementCategoryId === 1 && e.entitlementStatusId === 1 && Date.parse(e.expirationDate) > Date.now())
        .map<[number, QuickEntitlement]>(e => [e.entitlementTypeId, e])
    ).values());

  public getEntitlementTypeId = (item: { entitlementTypeId?: string }): string =>
    item.entitlementTypeId ?? "";
  
  public getEntitlementDescription = (item) : string => {
    const entitlement = this.props.allEntitlementTypes?.get(this.getEntitlementTypeId(item))
    return ((entitlement?.seasonDescription || '') + ' ' + entitlement?.publicDescription)
  };

  public onEntitlementChange = (entitlement) => this.props.onChange(this.props.guestId, entitlement);

  public render = () => (
    <TabSelectDiv onSelect={this.props.onSelect(this.props.guest!, isNilOrEmpty(this.props.eligibilityError))}
                  rootCss={GuestPrintSelect(this.props.eligibilityError, this.props.isSelected)}>
      <Column rootCss={css`padding: 10px 20px;`}>
        <Row alignItems='center'
            rootCss={css`
              ${ROW_SPACING};
              ${this.props.eligibilityError ? css`opacity: .5;` : ''};
            `}>
          <input type='checkbox'
                checked={this.props.isSelected}
                css={css`
                  ${onIe() ? '' : css`margin-right: 8px !important`};
                  ${ieCheckbox};
                  zoom: 2;
                `}/>

          <StatusButton width={75}
                        height={75}
                        borderWidth={3}
                        fillMode='fill'
                        borderRadius={CIRCULAR_RADIUS}
                        borderColor={hasPhoto(this.props.guest) ? GREEN_CREEK : RED_CREEK}
                        isLoading={this.props.loadingPhoto!}
                        handleClick={doNothing}
                        toolTipText=""
                        image={
                          R.path([ 'guest', 'photo', 'imgSmall' ], this.props)
                          || R.path([ 'guest', 'photo', 'imgLarge' ], this.props)
                          || profileStub
                        }
                        rootCss={GuestPrintStatusButton}/>
          <span title={fullName(this.props.guest!)} css={NameText}>{fullName(this.props.guest!)}</span>

          { this.props.enablePassReprintNotifications && 
            <div 
              title={this.state.passReprintFee === undefined ? getResource(this.props.resources, 'REPRINT_COUNT_LOAD_ERROR') : undefined} 
              css={ReprintFeeBox(this.state.passReprintFee)}>
                {this.state.passReprintFee !== undefined ? `$${this.state.passReprintFee}` : undefined}
            </div>
          }

          <div css={css`width: 350px;`}>
            <Dropdown
              resources={this.props.resources}
              items={this.entitlements ? this.entitlements : []}
              getKey={this.getEntitlementTypeId}
              getValue={this.getEntitlementTypeId}
              getLabel={this.getEntitlementDescription}
              noItemsMessage={getResource(this.props.resources, 'PRINT_NO_MEDIA')}
              onChange={this.onEntitlementChange}
              selectedItem={this.props.selectedEntitlements.get(this.props.guestId)}
              rootCss={css`width: 350px; max-width: 100%;`}
            />
          </div>
        </Row>
      <ErrorView error={this.props.eligibilityError}
                 boxStyle={BoxedErrorInverseNoBorder}
                 rootCss={css`user-select: none; padding-top: 10px;`}/>
      </Column>
    </TabSelectDiv>
  );
});
